import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../../firebase';
// Helper to determine the correct login redirect based on userType
const getLoginRedirect = (params?: string): string => {
  const storedUserType = localStorage.getItem('userType');
  const suffix = params ? `?${params}` : '';
  if (storedUserType === 'student') {
    return `/student-login${suffix}`;
  }
  // Default to teacher flow
  return `/teacher-login${suffix}`;
};
// Helper to determine the unauthenticated fallback redirect after successful verification
const getUnauthenticatedVerifiedRedirect = (): string => {
  const storedUserType = localStorage.getItem('userType');
  if (storedUserType === 'student') {
    // Send students directly to student login with verified flag
    return '/student-login?verified=true';
  }
  // Teachers get the landing page with auto-open login modal (existing behavior)
  return '/?openLogin=true&verified=true';
};
export const EmailVerifiedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Don't use context for immediate decisions - use auth.currentUser directly
  const {
    userType,
    setVerificationInProgress
  } = useAuth();
  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        const actionCode = searchParams.get('oobCode');
        const mode = searchParams.get('mode');
        console.log('[EmailVerified] ========================================');
        console.log('[EmailVerified] VERIFICATION LINK OPENED');
        console.log('[EmailVerified] ========================================');
        console.log('[EmailVerified] Mode:', mode);
        console.log('[EmailVerified] Has oobCode:', !!actionCode);
        console.log('[EmailVerified] Current URL:', window.location.href);
        console.log('[EmailVerified] localStorage userType:', localStorage.getItem('userType'));
        // EMAIL VERIFICATION FIX: Flag that verification is in progress
        // This prevents the cross-tab sign-out detection in AuthContext from
        // misinterpreting token refresh / reload as a sign-out and redirecting to '/'
        setVerificationInProgress(true);
        console.log('[EmailVerified] ✓ Verification guard enabled');
        // If this is an email verification action, process it immediately
        if (mode === 'verifyEmail' && actionCode) {
          console.log('[EmailVerified] ✓ Processing verification code');
          try {
            // Apply the action code to verify the email
            await applyActionCode(auth, actionCode);
            console.log('[EmailVerified] ✓ Email verified successfully');
            // Force reload the current user to get updated emailVerified status
            if (auth.currentUser) {
              await auth.currentUser.reload();
              await auth.currentUser.getIdToken(true); // Force token refresh
              console.log('[EmailVerified] ✓ User reloaded and token refreshed');
              console.log('[EmailVerified] Email verified status:', auth.currentUser.emailVerified);
            }
          } catch (error: any) {
            console.error('[EmailVerified] ✗ Error applying action code:', error);
            console.error('[EmailVerified] Error code:', error.code);
            console.error('[EmailVerified] Error message:', error.message);
            // If error is "invalid-action-code", the link may have been used already
            if (error.code === 'auth/invalid-action-code') {
              console.log('[EmailVerified] Link already used or expired, continuing to redirect');
            } else {
              // For other errors, redirect to the correct login page based on role
              const loginRedirect = getLoginRedirect('error=verification_failed');
              console.log('[EmailVerified] → Redirecting to login due to error:', loginRedirect);
              setVerificationInProgress(false);
              window.location.replace(loginRedirect);
              return;
            }
          }
        }
        // Small delay to ensure auth state is fully updated
        await new Promise(resolve => setTimeout(resolve, 500));
        // Clear the verification guard now that we're about to redirect
        setVerificationInProgress(false);
        console.log('[EmailVerified] ✓ Verification guard cleared');
        // CRITICAL: Use auth.currentUser directly, not React context
        const user = auth.currentUser;
        console.log('[EmailVerified] Determining redirect destination...');
        console.log('[EmailVerified] User authenticated:', !!user);
        if (user) {
          console.log('[EmailVerified] ✓ User authenticated:', user.uid);
          // Get userType from custom claims or localStorage
          let redirectUserType = userType;
          if (!redirectUserType) {
            // Try to get from custom claims
            try {
              const idTokenResult = await user.getIdTokenResult(true);
              redirectUserType = idTokenResult.claims.userType as string;
              console.log('[EmailVerified] UserType from claims:', redirectUserType);
            } catch (error) {
              console.error('[EmailVerified] Error getting claims:', error);
            }
          }
          if (!redirectUserType) {
            // Fallback to localStorage
            redirectUserType = localStorage.getItem('userType') || '';
            console.log('[EmailVerified] UserType from localStorage:', redirectUserType);
          }
          let redirectUrl = '/';
          if (redirectUserType === 'teacher') {
            redirectUrl = '/teacher-dashboard';
            console.log('[EmailVerified] → Redirecting to Teacher Dashboard');
          } else if (redirectUserType === 'student') {
            redirectUrl = '/student-dashboard';
            console.log('[EmailVerified] → Redirecting to Student Dashboard');
          } else {
            console.log('[EmailVerified] → Unknown user type, redirecting to home');
          }
          // Use window.location.replace for instant redirect (no history entry)
          console.log('[EmailVerified] Executing redirect to:', redirectUrl);
          window.location.replace(redirectUrl);
        } else {
          console.log('[EmailVerified] ✗ No user authenticated');
          // Store verification success flag for auto-redirect after login
          localStorage.setItem('emailJustVerified', 'true');
          localStorage.setItem('verificationTimestamp', Date.now().toString());
          // ROLE-AWARE REDIRECT: Send students to student login, teachers to landing page with modal
          const verifiedRedirect = getUnauthenticatedVerifiedRedirect();
          console.log('[EmailVerified] → Redirecting to:', verifiedRedirect);
          window.location.replace(verifiedRedirect);
        }
      } catch (err: any) {
        console.error('[EmailVerified] ✗ Unexpected error:', err);
        setVerificationInProgress(false);
        // On error, redirect to the correct login page based on role
        const loginRedirect = getLoginRedirect();
        console.log('[EmailVerified] → Error fallback redirect to:', loginRedirect);
        window.location.replace(loginRedirect);
      }
    };
    handleEmailVerification();
  }, [userType, searchParams, setVerificationInProgress]);
  // Minimal loading UI - just a spinner, no delay
  return <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-3932">
      <div className="flex flex-col items-center" data-id="element-3933">
        <div className="w-12 h-12 border-4 border-[#e63946] border-t-transparent rounded-full animate-spin" data-id="element-3934"></div>
        <p className="mt-4 text-white text-sm" data-id="element-3935">Verifying your email...</p>
      </div>
    </div>;
};